/**
 * AgenticED Admin Portal - Main App
 * @version 2.0.0 - OAuth + 2FA authentication
 */

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { PartnerPortalAuthGuard } from './pages/partner-portal/PartnerPortalLayout.tsx';
import { FoundationPortalAuthGuard } from './pages/foundation/FoundationPortalLayout.tsx';
import agenticedLogo from './assets/agenticed-logo.png';
import apiClient from './api/client';
import authService from './services/authService.js';

// Auth Pages — kept in main bundle (critical login flow, tiny)
import OAuthCallback from './pages/auth/OAuthCallback';
import TwoFactorVerify from './pages/auth/TwoFactorVerify';
// SPRINT_SATURDAY_FINISH Stream 1.A: MFA enrollment interstitial.
// Kept in the main auth bundle (critical post-login path, tiny).
import MfaEnrollmentInterstitial from './pages/auth/MfaEnrollmentInterstitial.tsx';

// ============================================================================
// LAZY-LOADED PORTAL PAGES
// ============================================================================
// Each portal's pages are grouped into a single chunk by manualChunks in
// vite.config.js. First navigation to a portal loads its chunk; all subsequent
// navigation within that portal is instant (already cached).

// --- Vendor Portal (→ "vendor" chunk) ---
const VendorDashboard = lazy(() => import('./pages/vendor/VendorDashboard.tsx'));
const VendorLogin = lazy(() => import('./pages/vendor/Login.tsx'));
const VendorRegister = lazy(() => import('./pages/vendor/Register.tsx'));
const VendorGettingStarted = lazy(() => import('./pages/vendor/GettingStarted.tsx'));
const VendorSandbox = lazy(() => import('./pages/vendor/Sandbox.tsx'));
const VendorSandboxLogs = lazy(() => import('./pages/vendor/SandboxLogs.tsx'));
const VendorSandboxData = lazy(() => import('./pages/vendor/SandboxData.tsx'));
const VendorAgents = lazy(() => import('./pages/vendor/Agents.tsx'));
const VendorAgentBuilder = lazy(() => import('./pages/vendor/AgentBuilder.tsx'));
const VendorAgentDetail = lazy(() => import('./pages/vendor/AgentDetail.tsx'));
const VendorCertification = lazy(() => import('./pages/vendor/Certification.tsx'));
const VendorSubmissionDetail = lazy(() => import('./pages/vendor/SubmissionDetail.tsx'));
const VendorDocumentation = lazy(() => import('./pages/vendor/Documentation.tsx'));
const VendorBilling = lazy(() => import('./pages/vendor/Billing.tsx'));
const VendorSupport = lazy(() => import('./pages/vendor/Support.tsx'));
const VendorAnalytics = lazy(() => import('./pages/vendor/Analytics.tsx'));
const VendorUsage = lazy(() => import('./pages/vendor/Usage.tsx'));
const VendorSettings = lazy(() => import('./pages/vendor/Settings.tsx'));
const VendorSetupTwoFactor = lazy(() => import('./pages/vendor/SetupTwoFactor.tsx'));
const VendorUsers = lazy(() => import('./pages/vendor/Users.tsx'));
const VendorStandardsProfile = lazy(() => import('./pages/vendor/StandardsProfile.tsx'));
const VendorCascadeMetadata = lazy(() => import('./pages/vendor/CascadeMetadata.tsx'));
const VendorRegistryPreview = lazy(() => import('./pages/vendor/RegistryPreview.tsx'));
const VendorCertificationReadiness = lazy(() => import('./pages/vendor/CertificationReadiness.tsx'));
const VendorForgotPassword = lazy(() => import('./pages/vendor/ForgotPassword.tsx'));
const VendorMarketplaceListing = lazy(() => import('./pages/vendor/MarketplaceListing.tsx'));

// --- Guardian Tenant Access (GTA) — vendor-facing tenant-containment product ---
// GTA-native surfaces (own design system, scoped .gta-root — NOT the district
// Tailwind). The GTA console shell owns /gta and derives every per-surface route from
// the session role: vendors get the vendor console, district people get the org fleet
// home and its per-connection surfaces (custody, recovery, audit log, …), all inside
// the shell and all authenticated by the district session identity.
const GtaConsoleRoutes = lazy(() => import('./gta/console').then((m) => ({ default: m.GtaConsoleRoutes })));
// GTA sign-in surface — replaces the platform Admin Portal login on the GTA env only.
const GtaLogin = lazy(() => import('./pages/gta/GtaLogin.tsx'));
import { isResponseHost as isResponseHostname } from './response/host';

// GTA env detection at /login: no session exists pre-login, so the only signal that
// mirrors the server's isGtaEnv (services/gta/GtaLoginService.isGtaEnv) is the hostname.
// Same regex — gta.agenticed.com / gta-dev.agenticed.com match; the platform domains do
// not, so platform login is untouched everywhere else.
function isGtaHost() {
    return /(^|\.)gta(-|\.)/i.test(window.location.hostname);
}

// --- RESPONSE staff portal (board follow-up & accountability) ---
// Same structure as GTA above and deliberately NOT the same identity: RESPONSE is
// an independent lineage with its own database and session mechanics, so its
// console authenticates against RESPONSE rather than the platform authService.
// See admin/src/response/identity.ts for why that distinction is load-bearing.
const ResponseConsoleRoutes = lazy(() => import('./response/console').then((m) => ({ default: m.ResponseConsoleRoutes })));
const ResponseLogin = lazy(() => import('./pages/response/ResponseLogin.tsx'));

// RESPONSE env detection at /login, for the reason GTA's exists: pre-login there
// is no session, so the hostname is the only signal. The predicate lives in
// response/host.ts so the client and the server's isResponseEnv can be read
// against each other and tested without a DOM.
function isResponseHost() {
    return isResponseHostname(window.location.hostname);
}

// /login renders the GTA sign-in on a GTA host, the RESPONSE sign-in on a
// RESPONSE host, and the platform LoginPage otherwise. The two product hosts are
// mutually exclusive by their patterns, and GTA is tested first only because it
// shipped first — neither matches the other's hosts.
function LoginRoute() {
    if (isGtaHost()) return <GtaLogin />;
    if (isResponseHost()) return <ResponseLogin />;
    return <LoginPage />;
}

// --- Foundation Portal (→ "foundation" chunk) ---
// Sibling to Vendor / Partner. Two-level navigation per architect (PR-1):
// foundation admin lands on Dashboard → drills into a specific Initiative.
// Per-initiative impact view renders cohort-min suppression honestly.
const FoundationLogin = lazy(() => import('./pages/foundation/Login.tsx'));
const FoundationPortalSwitch = lazy(() => import('./pages/foundation/PortalSwitch.tsx'));
const FoundationRegister = lazy(() => import('./pages/foundation/Register.tsx'));
const FoundationDashboard = lazy(() => import('./pages/foundation/FoundationDashboard.tsx'));
const FoundationInitiatives = lazy(() => import('./pages/foundation/Initiatives.tsx'));
const FoundationInitiativeImpact = lazy(() => import('./pages/foundation/InitiativeImpact.tsx'));
const FoundationDistrictDetail = lazy(() => import('./pages/foundation/DistrictDetail.tsx'));
const FoundationPrograms = lazy(() => import('./pages/foundation/Programs.tsx'));
const FoundationRecipients = lazy(() => import('./pages/foundation/Recipients.tsx'));
const FoundationCodes = lazy(() => import('./pages/foundation/Codes.tsx'));
const FoundationTeam = lazy(() => import('./pages/foundation/Team.tsx'));
const FoundationSettings = lazy(() => import('./pages/foundation/Settings.tsx'));
const FoundationComplianceConfig = lazy(() => import('./pages/foundation/ComplianceConfig.tsx'));
const FoundationDisbursement = lazy(() => import('./pages/foundation/Disbursement.tsx'));
const FoundationTrustSecurity = lazy(() => import('./pages/foundation/TrustSecurity.tsx'));

// --- District Portal (→ "district" chunk) ---
const DistrictDashboard = lazy(() => import('./pages/district/Dashboard'));
const DistrictCIODashboard = lazy(() => import('./pages/district/CIOObservability.tsx'));
const DistrictTenantAccessGuardian = lazy(() => import('./pages/district/TenantAccessGuardian.tsx'));
const DistrictSuperintendentDashboard = lazy(() => import('./pages/district/SuperintendentDashboard.tsx'));
const DistrictCounselorDashboard = lazy(() => import('./pages/district/CounselorDashboard.tsx'));
const DistrictProgramReport = lazy(() => import('./pages/district/ProgramReport.tsx'));
const DistrictProgramReportDetail = lazy(() => import('./pages/district/ProgramReportDetail.tsx'));
// Phase 1.6: DistrictWorkspace is no longer routed. The /district/workspace
// route now Navigate-redirects to /workspace (the Canvas SPA). The component
// file (admin/src/pages/district/Workspace.tsx) is intentionally kept on
// disk for one or two release cycles in case a quick rollback is needed.
const DistrictGrowth = lazy(() => import('./pages/district/Growth'));
const DistrictValue = lazy(() => import('./pages/district/Value'));
const DistrictBilling = lazy(() => import('./pages/district/Billing'));
const DistrictAnalytics = lazy(() => import('./pages/district/Analytics'));
const DistrictAgents = lazy(() => import('./pages/district/Agents'));
const DistrictUsers = lazy(() => import('./pages/district/Users'));
const DistrictEscalations = lazy(() => import('./pages/district/Escalations'));
const DistrictConsent = lazy(() => import('./pages/district/Consent'));
const DistrictAudit = lazy(() => import('./pages/district/Audit'));
const DistrictCompliance = lazy(() => import('./pages/district/Compliance'));
const DistrictSettings = lazy(() => import('./pages/district/Settings'));
const DistrictLanguages = lazy(() => import('./pages/district/Languages.tsx'));
const DistrictCalendar = lazy(() => import('./pages/district/Calendar'));
const DistrictKnowledge = lazy(() => import('./pages/district/Knowledge'));
const KnowledgeContribute = lazy(() => import('./pages/district/KnowledgeContribute'));
const DistrictTranslations = lazy(() => import('./pages/content/TranslationAdmin.tsx'));
const DistrictBenefits = lazy(() => import('./pages/district/Benefits'));
const DistrictOpportunities = lazy(() => import('./pages/district/Opportunities'));
const DistrictIntegrations = lazy(() => import('./pages/district/Integrations.tsx'));
const DistrictInitiatives = lazy(() => import('./pages/district/InitiativeRegistry'));
const DistrictInvestmentMatrix = lazy(() => import('./pages/district/InvestmentMatrix'));
const DistrictStrategicPlanner  = lazy(() => import('./pages/district/StrategicPlanner.tsx'));
const DistrictStrategicConstrained = lazy(() => import('./pages/district/StrategicPlannerConstrained.tsx'));
const DistrictBudgetScenario = lazy(() => import('./pages/district/BudgetScenario'));
const DistrictEndpointAgents = lazy(() => import('./pages/district/EndpointAgents'));
const TeacherStudents = lazy(() => import('./pages/district/TeacherStudents'));
const TeacherAttendance = lazy(() => import('./pages/district/TeacherAttendance'));
const TeacherAssignments = lazy(() => import('./pages/district/TeacherAssignments'));
const TeacherMessages = lazy(() => import('./pages/district/TeacherMessages'));
const TeacherStudentLogin = lazy(() => import('./pages/district/TeacherStudentLogin'));
const DistrictWorkflowList = lazy(() => import('./pages/district/WorkflowList.tsx'));
const DistrictPartnerNetwork = lazy(() => import('./pages/district/PartnerNetwork.tsx'));
// Sprint 4 PR-4d — district↔partner contracts admin surface
const DistrictContracts = lazy(() => import('./pages/district/Contracts.tsx'));
const RegistryCompare = lazy(() => import('./pages/district/RegistryCompare.tsx'));
const MarketplaceAdmin = lazy(() => import('./pages/marketplace/MarketplaceAdmin.tsx'));

// Partner Portal
const PartnerPortalLogin        = lazy(() => import('./pages/partner-portal/Login.tsx'));
const PartnerPortalRegister     = lazy(() => import('./pages/partner-portal/Register.tsx'));
const PartnerPortalSetup2FA     = lazy(() => import('./pages/partner-portal/Setup2FA.tsx'));
const PartnerPortalDashboard    = lazy(() => import('./pages/partner-portal/Dashboard.tsx'));
const PartnerPortalPrograms     = lazy(() => import('./pages/partner-portal/Programs.tsx'));
const PartnerPortalReferrals    = lazy(() => import('./pages/partner-portal/Referrals.tsx'));
const PartnerPortalApplications = lazy(() => import('./pages/partner-portal/Applications.tsx'));
const PartnerPortalRoster       = lazy(() => import('./pages/partner-portal/Roster.tsx'));
const PartnerPortalAttendance   = lazy(() => import('./pages/partner-portal/Attendance.tsx'));
const PartnerPortalAnalytics    = lazy(() => import('./pages/partner-portal/Analytics.tsx'));
const PartnerPortalBilling      = lazy(() => import('./pages/partner-portal/Billing.tsx'));
const PartnerPortalTeam         = lazy(() => import('./pages/partner-portal/Team.tsx'));
const PartnerPortalSettings     = lazy(() => import('./pages/partner-portal/Settings.tsx'));
const PartnerPortalProgramProfile = lazy(() => import('./pages/partner-portal/ProgramProfile.tsx'));
const PartnerPortalOnboarding   = lazy(() => import('./pages/partner-portal/Onboarding.tsx'));
const PartnerPortalCredentials  = lazy(() => import('./pages/partner-portal/Credentials.tsx'));
const PartnerPortalHowItWorks   = lazy(() => import('./pages/partner-portal/HowItWorks.tsx'));
const PartnerPortalGuide        = lazy(() => import('./pages/partner-portal/Guide.tsx'));
const PartnerPortalSwitch = lazy(() => import('./pages/partner-portal/PortalSwitch.tsx'));
const PartnerIntakeForm = lazy(() => import('./pages/public/PartnerIntakeForm.tsx'));
const AcceptInvite = lazy(() => import('./pages/public/AcceptInvite.tsx'));
const CertifiedPartnerDashboard = lazy(() => import('./pages/vendor/CertifiedPartnerDashboard.tsx'));
const DistrictWorkflowBuilder = lazy(() => import('./pages/district/WorkflowBuilder.tsx'));
const DistrictWorkflowDetail = lazy(() => import('./pages/district/WorkflowDetail.tsx'));
const DistrictTemplateGallery = lazy(() => import('./pages/district/TemplateGallery.tsx'));
const DistrictWorkflowRunDetail = lazy(() => import('./pages/district/WorkflowRunDetail.tsx'));
const DistrictResourceCatalog = lazy(() => import('./pages/district/ResourceCatalog.tsx'));
const DistrictContextConfig = lazy(() => import('./pages/district/ContextConfig.tsx'));
const DistrictContextGraphPolicy = lazy(() => import('./pages/district/ContextGraphPolicy.tsx'));
const DistrictMtssSetup = lazy(() => import('./pages/district/MtssSetup.tsx'));
const DistrictBenefitCoverage = lazy(() => import('./pages/district/BenefitCoverage.tsx'));
const DistrictBenefitsReview = lazy(() => import('./pages/district/BenefitsReview.tsx'));
const DistrictTasks = lazy(() => import('./pages/district/Tasks.tsx'));
const DistrictCascadePortfolio = lazy(() => import('./pages/district/CascadePortfolio.tsx'));
const NewInitiativeWizard = lazy(() => import('./pages/district/NewInitiativeWizard.tsx'));
const DistrictCascadeInitiative = lazy(() => import('./pages/district/CascadeInitiative.tsx'));
const EdTechRegistry = lazy(() => import('./pages/district/EdTechRegistry.tsx'));
const DistrictTechStackReport = lazy(() => import('./pages/district/TechStackReport'));
const DistrictTechStackInventory = lazy(() => import('./pages/district/tech-stack/TechStackInventory'));
const DistrictTechStackUtilization = lazy(() => import('./pages/district/tech-stack/TechStackUtilization'));
const DistrictTechStackROI = lazy(() => import('./pages/district/tech-stack/TechStackROI'));
const DistrictROIAssumptions = lazy(() => import('./pages/district/ROIAssumptions.tsx'));

// --- Workspace Admin (→ "district" chunk, same portal) ---
const RoleTemplatesPage = lazy(() => import('./pages/workspace/RoleTemplates'));
const RoleTemplateEditor = lazy(() => import('./pages/workspace/RoleTemplateEditor'));
const LockManagerPage = lazy(() => import('./pages/workspace/LockManager'));
const PreviewModePage = lazy(() => import('./pages/workspace/PreviewMode'));
const ItemsCatalogPage = lazy(() => import('./pages/workspace/ItemsCatalog'));
const WebPartBuilderPage = lazy(() => import('./pages/workspace/WebPartBuilder'));
const VendorAppPage = lazy(() => import('./pages/workspace/VendorAppPage.tsx'));
const LayoutLibraryPage = lazy(() => import('./pages/workspace/LayoutLibrary'));

// --- Staff Portal (→ "staff" chunk) ---
const StaffDashboard = lazy(() => import('./pages/staff/Dashboard'));
const StaffTenants = lazy(() => import('./pages/staff/TenantsOverview'));
const StaffTenantDetail = lazy(() => import('./pages/staff/TenantDetail'));
const StaffPlatformValue = lazy(() => import('./pages/staff/PlatformValue'));
const StaffRevenue = lazy(() => import('./pages/staff/Revenue'));
const StaffPricing = lazy(() => import('./pages/staff/Pricing.tsx'));
const StaffCertificationReview = lazy(() => import('./pages/staff/CertificationReview.tsx'));
const StaffVouchers = lazy(() => import('./pages/staff/Vouchers'));
const StaffSupportQueue = lazy(() => import('./pages/staff/SupportQueue'));
const StaffVendors = lazy(() => import('./pages/staff/VendorRegistry'));
const StaffAgentRegistry = lazy(() => import('./pages/staff/AgentRegistry'));
const StaffSystemHealth = lazy(() => import('./pages/staff/SystemHealth'));
const StaffDriftEvents = lazy(() => import('./pages/staff/DriftEvents'));
const StaffFeatureFlags = lazy(() => import('./pages/staff/FeatureFlags'));
const StaffCobrowseTemplates = lazy(() => import('./pages/staff/CobrowseTemplates'));
const StaffAuditLog = lazy(() => import('./pages/staff/InternalAuditLog'));
const StaffUsers = lazy(() => import('./pages/staff/Users'));
const StaffRegistryData = lazy(() => import('./pages/staff/RegistryDataManagement'));
const RegistryOps = lazy(() => import('./pages/staff/RegistryOps.tsx'));
const StaffPartnerCuration = lazy(() => import('./pages/staff/PartnerCuration.tsx'));
const StaffPartnerCurationDetail = lazy(() => import('./pages/staff/PartnerCurationDetail.tsx'));
const MarketplacePartnerVetting = lazy(() => import('./pages/marketplace/PartnerVetting.tsx'));
const MarketplacePartnerVettingDetail = lazy(() => import('./pages/marketplace/PartnerVettingDetail.tsx'));

// Role-based routing config
import { isRouteAllowed, getHomeForRole } from './config/pageRoles.js';

// ============================================================================
// AUTH GUARD
// ============================================================================

function AuthGuard({ children, requiredType, requiredRoles, fallback }) {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!authService.isLoggedIn()) {
            // If a fallback is provided (e.g. public landing page), go there instead of /login
            navigate(fallback || '/login', { state: { from: location } });
            return;
        }

        // Portal type check (staff / district / vendor / foundation)
        if (requiredType && authService.getType() !== requiredType) {
            const currentType = authService.getType();
            if (currentType === 'vendor') navigate('/vendor');
            else if (currentType === 'foundation') navigate('/foundation');
            else if (currentType === 'staff') navigate('/staff');
            else navigate('/district');
            return;
        }

        // Role check within district portal
        if (requiredType === 'district') {
            const role = authService.getRole();
            if (requiredRoles && !requiredRoles.includes(role)) {
                navigate(getHomeForRole(role), { replace: true });
                return;
            }
            // Also check route-level permissions from pageRoles config
            if (!requiredRoles && !isRouteAllowed(role, location.pathname)) {
                navigate(getHomeForRole(role), { replace: true });
            }
        }
    }, [navigate, location, requiredType, requiredRoles]);

    if (!authService.isLoggedIn()) return null;
    return children;
}


// ============================================================================
// LOGIN PAGE
// ============================================================================

function LoginPage() {
    const navigate = useNavigate();
    const [loginType, setLoginType] = useState('district');
    const [apiKey, setApiKey] = useState('');
    const [tenantSlug, setTenantSlug] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [demoPassword, setDemoPassword] = useState('');

    const handleDemoLogin = async (type) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/auth/demo-token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: demoPassword, portalType: type }),
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Invalid demo password');
            }

            const data = await response.json();

            // The session guard must not outlive the TOKEN. This read
            // `Date.now() + 24h` while /api/auth/demo-token signs with
            // `expiresIn: '8h'`, so for sixteen hours the client believed it was
            // logged in — route guards passed and the sidebar rendered the
            // persona — while every API call 401'd. A surface that looks
            // authenticated and cannot fetch is the worst of both: the operator
            // sees their own name and a wall of failures, and nothing says why.
            //
            // Derived from the token's own `exp` claim, so the two cannot drift
            // apart again. If the claim is unreadable we fall back to the
            // token's real 8h lifetime, never to a longer guess.
            const tokenExpiresAt = (() => {
                try {
                    const claims = JSON.parse(atob(data.token.split('.')[1]));
                    if (claims.exp) return new Date(claims.exp * 1000).toISOString();
                } catch { /* fall through to the conservative default */ }
                return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
            })();

            // Store session for authService (guards route access)
            localStorage.setItem('agenticed_admin_auth', JSON.stringify({
                token: data.token,
                type: type === 'staff' || type === 'vendor' ? type : 'district',
                demoRole: type,
                identifier: data.user.name || data.user.email,
                userId: data.user.userId,
                email: data.user.email,
                tenantId: data.user.tenantId,
                isSuperuser: true,
                isDemo: true,
                loggedInAt: Date.now(),
                expiresAt: tokenExpiresAt
            }));

            // Every data store and page reads `agenticed_session` FIRST and only
            // falls back to agenticed_admin_auth.token (16 read sites, e.g.
            // superintendentDashboardStore.ts and StrategicPlanner.tsx). Login
            // wrote only agenticed_admin_auth, so a stale agenticed_session left
            // by an earlier role switch SHADOWED the freshly minted token — and
            // logging out and back in did not clear it, which made the 401s
            // survive the one remedy anybody would try.
            localStorage.setItem('agenticed_session', data.token);

            // Store tenant branding for Layout to display correct logo
            if (data.tenant?.branding) {
                localStorage.setItem('agenticed_tenant_branding', JSON.stringify(data.tenant.branding));
            }

            // Store session token for apiClient (sends Bearer token on API calls)
            apiClient.setSessionToken(data.token);

            // Bridge vendor auth: also populate vendorAuthStore so vendor pages recognize the session
            if (type === 'vendor') {
                const { useVendorAuthStore } = await import('../../shared/stores/vendorAuthStore');
                useVendorAuthStore.getState().setVendor(
                    {
                        vendor_id: data.user.userId,
                        company_name: 'Demo Vendor',
                        contact_email: data.user.email,
                        status: 'active',
                    },
                    data.token
                );
            }

            if (type === 'staff' || type === 'vendor') {
                window.location.href = `/admin/${type}`;
            } else {
                // District roles — navigate to role-specific home page
                const homePath = getHomeForRole(type);
                window.location.href = `/admin${homePath}`;
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Check if already logged in — redirect to role-appropriate home
    useEffect(() => {
        if (authService.isLoggedIn()) {
            const type = authService.getType();
            if (type === 'vendor') navigate('/vendor');
            else if (type === 'staff') navigate('/staff');
            else {
                const role = authService.getRole();
                navigate(getHomeForRole(role));
            }
        }
    }, [navigate]);

    // Handle OAuth login (Google/Microsoft)
    const handleOAuthLogin = async (provider) => {
        setLoading(true);
        setError(null);

        try {
            let url;
            if (provider === 'google') {
                url = await authService.getGoogleAuthUrl(loginType, loginType === 'district' ? tenantSlug : null);
            } else {
                url = await authService.getMicrosoftAuthUrl(loginType, loginType === 'district' ? tenantSlug : null);
            }

            // Redirect to OAuth provider
            window.location.href = url;
        } catch (err) {
            const providerName = provider === 'google' ? 'Google' : 'Microsoft';
            setError(`${providerName} OAuth not configured. Use "Demo Login" button below, or configure OAuth credentials in AWS Secrets Manager.`);
            setLoading(false);
        }
    };

    // Handle vendor API key login
    const handleVendorLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!apiKey || apiKey.trim() === '') {
                throw new Error('API key is required');
            }

            await authService.loginWithApiKey(apiKey);
            apiClient.setApiKey(apiKey);
            navigate('/vendor');
        } catch (err) {
            setError(err.message || 'Invalid API key');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center mb-4">
                        <img
                            src={agenticedLogo}
                            alt="AgenticED"
                            className="h-12 w-auto"
                        />
                    </div>
                    <p className="text-slate-600">Admin Portal</p>
                </div>

                {/* Portal Selector */}
                <div className="flex mb-6 bg-white rounded-lg p-1 border border-slate-200">
                    <button
                        onClick={() => { setLoginType('staff'); setError(null); }}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                            loginType === 'staff'
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        Staff
                    </button>
                    <button
                        onClick={() => { setLoginType('district'); setError(null); }}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                            loginType === 'district'
                                ? 'bg-purple-100 text-purple-700'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        District
                    </button>
                    <button
                        onClick={() => { setLoginType('vendor'); setError(null); }}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                            loginType === 'vendor'
                                ? 'bg-teal-100 text-teal-700'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        Vendor
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Staff Login - OAuth only */}
                    {loginType === 'staff' && (
                        <div>
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-600 font-medium">AgenticED Internal</p>
                                <p className="text-sm text-blue-800">Platform management & oversight</p>
                            </div>

                            <p className="text-sm text-slate-600 mb-4 text-center">
                                Sign in with your AgenticED account
                            </p>

                            {/* Google OAuth Button */}
                            <button
                                onClick={() => handleOAuthLogin('google')}
                                disabled={loading}
                                className="w-full mb-3 py-2.5 px-4 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                {loading ? 'Redirecting...' : 'Continue with Google'}
                            </button>

                            {/* Microsoft OAuth Button */}
                            <button
                                onClick={() => handleOAuthLogin('microsoft')}
                                disabled={loading}
                                className="w-full py-2.5 px-4 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 23 23">
                                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                </svg>
                                {loading ? 'Redirecting...' : 'Continue with Microsoft'}
                            </button>

                            {/* Demo Login */}
                            <div className="mt-4 pt-4 border-t border-slate-200">
                                <p className="text-xs text-slate-500 mb-2 text-center">Demo Access</p>
                                <div className="flex gap-2">
                                    <input
                                        type="password"
                                        value={demoPassword}
                                        onChange={(e) => { setDemoPassword(e.target.value); setError(null); }}
                                        placeholder="Demo password"
                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleDemoLogin('staff')}
                                        className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 transition-colors whitespace-nowrap"
                                    >
                                        Demo Login
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* District Login - OAuth with optional tenant selection */}
                    {loginType === 'district' && (
                        <div>
                            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                                <p className="text-xs text-purple-600 font-medium">District Administrator</p>
                                <p className="text-sm text-purple-800">Manage agents, users & billing</p>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    District ID <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={tenantSlug}
                                    onChange={(e) => setTenantSlug(e.target.value)}
                                    placeholder="e.g., philadelphia-sd"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                                <p className="mt-1 text-xs text-slate-500">
                                    Your district will be detected automatically if not specified
                                </p>
                            </div>

                            {/* Google OAuth Button */}
                            <button
                                onClick={() => handleOAuthLogin('google')}
                                disabled={loading}
                                className="w-full mb-3 py-2.5 px-4 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                {loading ? 'Redirecting...' : 'Continue with Google'}
                            </button>

                            {/* Microsoft OAuth Button */}
                            <button
                                onClick={() => handleOAuthLogin('microsoft')}
                                disabled={loading}
                                className="w-full py-2.5 px-4 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 23 23">
                                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                </svg>
                                {loading ? 'Redirecting...' : 'Continue with Microsoft'}
                            </button>

                            {/* Demo Login — Role-Specific */}
                            <div className="mt-4 pt-4 border-t border-slate-200">
                                <p className="text-xs text-slate-500 mb-2 text-center">Demo Access — Choose a Role</p>
                                <div className="flex gap-2 mb-3">
                                    <input
                                        type="password"
                                        value={demoPassword}
                                        onChange={(e) => { setDemoPassword(e.target.value); setError(null); }}
                                        placeholder="Demo password"
                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => handleDemoLogin('superintendent')} disabled={loading}
                                        className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 transition-colors disabled:opacity-50">
                                        Superintendent
                                    </button>
                                    <button type="button" onClick={() => handleDemoLogin('principal')} disabled={loading}
                                        className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100 transition-colors disabled:opacity-50">
                                        Principal
                                    </button>
                                    <button type="button" onClick={() => handleDemoLogin('cio')} disabled={loading}
                                        className="px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors disabled:opacity-50">
                                        CIO
                                    </button>
                                    <button type="button" onClick={() => handleDemoLogin('counselor')} disabled={loading}
                                        className="px-3 py-2 bg-rose-50 text-rose-700 rounded-lg text-xs font-medium hover:bg-rose-100 transition-colors disabled:opacity-50">
                                        Counselor
                                    </button>
                                    <button type="button" onClick={() => handleDemoLogin('teacher')} disabled={loading}
                                        className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors disabled:opacity-50">
                                        Teacher
                                    </button>
                                    <button type="button" onClick={() => handleDemoLogin('district')} disabled={loading}
                                        className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors disabled:opacity-50">
                                        {loading ? '...' : 'Full Admin'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Vendor Login - API Key */}
                    {loginType === 'vendor' && (
                        <form onSubmit={handleVendorLogin}>
                            <div className="mb-4 p-3 bg-teal-50 rounded-lg">
                                <p className="text-xs text-teal-600 font-medium">EdTech Vendor</p>
                                <p className="text-sm text-teal-800">Manage agents & certifications</p>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
                                <input
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => { setApiKey(e.target.value); setError(null); }}
                                    placeholder="Enter your vendor API key"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-2.5 px-4 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? 'Signing in...' : 'Sign In with API Key'}
                            </button>

                            <div className="mt-4 text-center">
                                <p className="text-sm text-slate-600">
                                    New vendor?{' '}
                                    <a href="/admin/vendor/register" className="text-teal-600 hover:text-teal-700 font-medium">
                                        Register for an account
                                    </a>
                                </p>
                            </div>

                            {/* Demo Login */}
                            <div className="mt-4 pt-4 border-t border-slate-200">
                                <p className="text-xs text-slate-500 mb-2 text-center">Demo Access</p>
                                <div className="flex gap-2">
                                    <input
                                        type="password"
                                        value={demoPassword}
                                        onChange={(e) => { setDemoPassword(e.target.value); setError(null); }}
                                        placeholder="Demo password"
                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleDemoLogin('vendor')}
                                        className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 transition-colors whitespace-nowrap"
                                    >
                                        Demo Login
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                <p className="mt-6 text-center text-xs text-slate-500">
                    &copy; 2026 AgenticED. Rails not trains.
                </p>
            </div>
        </div>
    );
}

// ============================================================================
// 404 PAGE
// ============================================================================

function NotFound() {
    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center">
            <div className="text-center">
                <h1 className="text-6xl font-bold text-slate-300 mb-4">404</h1>
                <p className="text-slate-600 mb-6">Page not found</p>
                {/* Use Link (not <a href="/login">) so the router resolves the
                    path under basename="/admin". A raw href="/login" navigates
                    to the root /login, which is outside the basename and renders
                    a blank page. */}
                <Link to="/login" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Go to Login
                </Link>
            </div>
        </div>
    );
}

// ============================================================================
// APP ROUTER
// ============================================================================

// Loading fallback — shown while a portal chunk is loading
function PageLoader() {
    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <p className="text-sm text-slate-500">Loading...</p>
            </div>
        </div>
    );
}

// Sprint 2 PR-2b.2: the partner portal moved from /cbo/* to /partner-portal/*.
// Legacy /cbo/* paths redirect so bookmarked URLs keep working through the
// Sprint 2 window; this redirect can be dropped once Sprint 2 has shipped.
function LegacyCboRedirect() {
    const location = useLocation();
    const target = location.pathname.replace(/^\/cbo(?=\/|$)/, '/partner-portal');
    return <Navigate to={`${target}${location.search}`} replace />;
}

function App() {
    return (
        <BrowserRouter basename="/admin">
            <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/login" element={<LoginRoute />} />

                {/* Auth Routes */}
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route path="/verify-2fa" element={<TwoFactorVerify />} />
                {/* SPRINT_SATURDAY_FINISH Stream 1.A: MFA enrollment landing page.
                    Login handler redirects here when mfa_enrollment_due=TRUE. */}
                <Route path="/auth/enroll" element={<MfaEnrollmentInterstitial />} />
                
                {/* AgenticED Staff Portal (Internal Admin) */}
                <Route path="/staff" element={<AuthGuard requiredType="staff"><StaffDashboard /></AuthGuard>} />
                <Route path="/staff/tenants" element={<AuthGuard requiredType="staff"><StaffTenants /></AuthGuard>} />
                <Route path="/staff/tenants/:tenantId" element={<AuthGuard requiredType="staff"><StaffTenantDetail /></AuthGuard>} />
                <Route path="/staff/platform-value" element={<AuthGuard requiredType="staff"><StaffPlatformValue /></AuthGuard>} />
                <Route path="/staff/revenue" element={<AuthGuard requiredType="staff"><StaffRevenue /></AuthGuard>} />
                <Route path="/staff/pricing" element={<AuthGuard requiredType="staff"><StaffPricing /></AuthGuard>} />
                <Route path="/staff/certification-review" element={<AuthGuard requiredType="staff"><StaffCertificationReview /></AuthGuard>} />
                <Route path="/staff/vouchers" element={<AuthGuard requiredType="staff"><StaffVouchers /></AuthGuard>} />
                <Route path="/staff/support" element={<AuthGuard requiredType="staff"><StaffSupportQueue /></AuthGuard>} />
                <Route path="/staff/vendors" element={<AuthGuard requiredType="staff"><StaffVendors /></AuthGuard>} />
                <Route path="/staff/agents" element={<AuthGuard requiredType="staff"><StaffAgentRegistry /></AuthGuard>} />
                <Route path="/staff/health" element={<AuthGuard requiredType="staff"><StaffSystemHealth /></AuthGuard>} />
                <Route path="/staff/drift-events" element={<AuthGuard requiredType="staff"><StaffDriftEvents /></AuthGuard>} />
                <Route path="/staff/flags" element={<AuthGuard requiredType="staff"><StaffFeatureFlags /></AuthGuard>} />
                <Route path="/staff/cobrowse" element={<AuthGuard requiredType="staff"><StaffCobrowseTemplates /></AuthGuard>} />
                <Route path="/staff/audit" element={<AuthGuard requiredType="staff"><StaffAuditLog /></AuthGuard>} />
                <Route path="/staff/users" element={<AuthGuard requiredType="staff"><StaffUsers /></AuthGuard>} />
                <Route path="/staff/registry-data" element={<AuthGuard requiredType="staff"><StaffRegistryData /></AuthGuard>} />
                <Route path="/staff/registry" element={<AuthGuard requiredType="staff"><EdTechRegistry mode="staff" /></AuthGuard>} />
                <Route path="/staff/registry-ops" element={<AuthGuard requiredType="staff"><RegistryOps /></AuthGuard>} />
                <Route path="/staff/partner-curation" element={<AuthGuard requiredType="staff"><StaffPartnerCuration /></AuthGuard>} />
                <Route path="/staff/partner-curation/:orgId" element={<AuthGuard requiredType="staff"><StaffPartnerCurationDetail /></AuthGuard>} />

                {/* Vendor Portal - Public Routes */}
                <Route path="/vendor/login" element={<VendorLogin />} />
                <Route path="/vendor/register" element={<VendorRegister />} />
                <Route path="/partner-intake/:districtSlug" element={<PartnerIntakeForm />} />
                <Route path="/partners/accept-invite/:token" element={<AcceptInvite />} />
                <Route path="/vendor/forgot-password" element={<VendorForgotPassword />} />

                {/* Guardian Tenant Access (GTA). /gta/* mounts the persistent console shell
                    that wraps every surface and derives its nav from the session role (a client
                    affordance only; the server enforces authority in the access predicate). */}
                <Route path="/gta/*" element={<GtaConsoleRoutes />} />

                {/* RESPONSE staff portal. One route, like GTA's: the console shell
                    owns everything under it and derives its surfaces from the
                    operator's grants. It admits nobody until RESPONSE mints a
                    staff session — the shell renders a refusal, never a nav. */}
                <Route path="/response/*" element={<ResponseConsoleRoutes />} />

                {/* Vendor Portal - Protected Routes */}
                <Route path="/vendor" element={<AuthGuard requiredType="vendor" fallback="/vendor/getting-started"><VendorDashboard /></AuthGuard>} />
                <Route path="/vendor/getting-started" element={<VendorGettingStarted />} />
                <Route path="/vendor/sandbox" element={<AuthGuard requiredType="vendor"><VendorSandbox /></AuthGuard>} />
                <Route path="/vendor/sandbox/logs" element={<AuthGuard requiredType="vendor"><VendorSandboxLogs /></AuthGuard>} />
                <Route path="/vendor/sandbox/data" element={<AuthGuard requiredType="vendor"><VendorSandboxData /></AuthGuard>} />
                <Route path="/vendor/agents" element={<AuthGuard requiredType="vendor"><VendorAgents /></AuthGuard>} />
                <Route path="/vendor/agents/new" element={<AuthGuard requiredType="vendor"><VendorAgentBuilder /></AuthGuard>} />
                <Route path="/vendor/agents/:id" element={<AuthGuard requiredType="vendor"><VendorAgentDetail /></AuthGuard>} />
                <Route path="/vendor/agents/:id/edit" element={<AuthGuard requiredType="vendor"><VendorAgentBuilder /></AuthGuard>} />
                <Route path="/vendor/certification" element={<AuthGuard requiredType="vendor"><VendorCertification /></AuthGuard>} />
                <Route path="/vendor/certification/readiness" element={<VendorCertificationReadiness />} />
                <Route path="/vendor/certification/:submissionId" element={<AuthGuard requiredType="vendor"><VendorSubmissionDetail /></AuthGuard>} />
                <Route path="/vendor/submissions/:submissionId" element={<AuthGuard requiredType="vendor"><VendorSubmissionDetail /></AuthGuard>} />
                <Route path="/vendor/docs" element={<VendorDocumentation />} />
                <Route path="/vendor/billing" element={<AuthGuard requiredType="vendor"><VendorBilling /></AuthGuard>} />
                <Route path="/vendor/portfolio" element={<AuthGuard requiredType="vendor"><CertifiedPartnerDashboard /></AuthGuard>} />
                <Route path="/vendor/support" element={<AuthGuard requiredType="vendor"><VendorSupport /></AuthGuard>} />
                <Route path="/vendor/analytics" element={<AuthGuard requiredType="vendor"><VendorAnalytics /></AuthGuard>} />
                <Route path="/vendor/usage" element={<AuthGuard requiredType="vendor"><VendorUsage /></AuthGuard>} />
                <Route path="/vendor/settings" element={<AuthGuard requiredType="vendor"><VendorSettings /></AuthGuard>} />
                <Route path="/vendor/setup-2fa" element={<AuthGuard requiredType="vendor"><VendorSetupTwoFactor /></AuthGuard>} />
                <Route path="/vendor/team" element={<AuthGuard requiredType="vendor"><VendorUsers /></AuthGuard>} />
                <Route path="/vendor/standards" element={<AuthGuard requiredType="vendor"><VendorStandardsProfile /></AuthGuard>} />
                <Route path="/vendor/cascade" element={<AuthGuard requiredType="vendor"><VendorCascadeMetadata /></AuthGuard>} />
                <Route path="/vendor/registry" element={<VendorRegistryPreview />} />
                <Route path="/vendor/marketplace" element={<AuthGuard requiredType="vendor"><VendorMarketplaceListing /></AuthGuard>} />

                {/* Foundation Portal — funder/sponsor surface (sibling to Vendor / Partner).
                    Mirrors Vendor's auth/onboarding pattern; backend gates on
                    requireFoundationContext (sponsorId from JWT, §A3 audited PR-1). */}
                <Route path="/foundation/login" element={<FoundationLogin />} />
                <Route path="/foundation/register" element={<FoundationRegister />} />
                {/* Superuser entry — platform admin login required (picker → /superuser-enter) */}
                <Route path="/foundation/switch" element={<FoundationPortalSwitch />} />
                <Route path="/foundation" element={<FoundationPortalAuthGuard><FoundationDashboard /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/initiatives" element={<FoundationPortalAuthGuard><FoundationInitiatives /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/initiatives/:initiativeId" element={<FoundationPortalAuthGuard><FoundationInitiativeImpact /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/districts/:tenantId" element={<FoundationPortalAuthGuard><FoundationDistrictDetail /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/programs" element={<FoundationPortalAuthGuard><FoundationPrograms /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/recipients" element={<FoundationPortalAuthGuard><FoundationRecipients /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/codes" element={<FoundationPortalAuthGuard><FoundationCodes /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/team" element={<FoundationPortalAuthGuard><FoundationTeam /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/settings" element={<FoundationPortalAuthGuard><FoundationSettings /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/compliance" element={<FoundationPortalAuthGuard><FoundationComplianceConfig /></FoundationPortalAuthGuard>} />
                <Route path="/foundation/disbursement" element={<FoundationPortalAuthGuard><FoundationDisbursement /></FoundationPortalAuthGuard>} />
                {/* Trust & Security: gated by the Security Officer's CIO session (apiClient JWT),
                    not the foundation portal token — so it is intentionally NOT wrapped in
                    FoundationPortalAuthGuard. The page handles 401/403 with a sign-in prompt. */}
                <Route path="/foundation/trust" element={<FoundationTrustSecurity />} />

                {/* District — Zone 1: My Workspace & Zone 2: My Growth (all roles).
                    Phase 1.6: /district/workspace now redirects to the Canvas
                    SPA at /workspace. The DistrictWorkspace component is left
                    in the codebase (orphaned) for one or two release cycles
                    in case rollback is needed. Subroutes
                    (/district/workspace/students, etc.) keep working — they
                    don't go through DistrictWorkspace. */}
                <Route path="/district/workspace" element={<Navigate to="/workspace" replace />} />
                <Route path="/district/workspace/students" element={<AuthGuard requiredType="district"><TeacherStudents /></AuthGuard>} />
                <Route path="/district/workspace/attendance" element={<AuthGuard requiredType="district"><TeacherAttendance /></AuthGuard>} />
                <Route path="/district/workspace/assignments" element={<AuthGuard requiredType="district"><TeacherAssignments /></AuthGuard>} />
                <Route path="/district/workspace/messages" element={<AuthGuard requiredType="district"><TeacherMessages /></AuthGuard>} />
                <Route path="/district/workspace/student-login" element={<AuthGuard requiredType="district"><TeacherStudentLogin /></AuthGuard>} />
                <Route path="/district/growth" element={<AuthGuard requiredType="district"><DistrictGrowth /></AuthGuard>} />

                {/* District Admin — Zone 3: Administration (role-gated) */}
                <Route path="/district" element={<AuthGuard requiredType="district"><DistrictDashboard /></AuthGuard>} />
                <Route path="/district/cio" element={<AuthGuard requiredType="district"><DistrictCIODashboard /></AuthGuard>} />
                <Route path="/district/guardian" element={<AuthGuard requiredType="district"><DistrictTenantAccessGuardian /></AuthGuard>} />
                <Route path="/district/superintendent" element={<AuthGuard requiredType="district"><DistrictSuperintendentDashboard /></AuthGuard>} />
                <Route path="/district/counselor" element={<AuthGuard requiredType="district"><DistrictCounselorDashboard /></AuthGuard>} />
                <Route path="/district/program-report" element={<AuthGuard requiredType="district"><DistrictProgramReport /></AuthGuard>} />
                <Route path="/district/program-report/:initiativeId" element={<AuthGuard requiredType="district"><DistrictProgramReportDetail /></AuthGuard>} />
                <Route path="/district/value" element={<AuthGuard requiredType="district"><DistrictValue /></AuthGuard>} />
                <Route path="/district/roi-assumptions" element={<AuthGuard requiredType="district"><DistrictROIAssumptions /></AuthGuard>} />
                <Route path="/district/billing" element={<AuthGuard requiredType="district"><DistrictBilling /></AuthGuard>} />
                <Route path="/district/analytics" element={<AuthGuard requiredType="district"><DistrictAnalytics /></AuthGuard>} />
                <Route path="/district/agents" element={<AuthGuard requiredType="district"><DistrictAgents /></AuthGuard>} />
                <Route path="/district/users" element={<AuthGuard requiredType="district"><DistrictUsers /></AuthGuard>} />
                <Route path="/district/escalations" element={<AuthGuard requiredType="district"><DistrictEscalations /></AuthGuard>} />
                <Route path="/district/consent" element={<AuthGuard requiredType="district"><DistrictConsent /></AuthGuard>} />
                <Route path="/district/audit" element={<AuthGuard requiredType="district"><DistrictAudit /></AuthGuard>} />
                <Route path="/district/compliance" element={<AuthGuard requiredType="district"><DistrictCompliance /></AuthGuard>} />
                <Route path="/district/calendar" element={<AuthGuard requiredType="district"><DistrictCalendar /></AuthGuard>} />
                <Route path="/district/knowledge" element={<AuthGuard requiredType="district"><DistrictKnowledge /></AuthGuard>} />
                <Route path="/district/knowledge/contribute" element={<AuthGuard requiredType="district"><KnowledgeContribute /></AuthGuard>} />
                <Route path="/district/translations" element={<AuthGuard requiredType="district"><DistrictTranslations /></AuthGuard>} />
                <Route path="/district/benefits" element={<AuthGuard requiredType="district"><DistrictBenefits /></AuthGuard>} />
                <Route path="/district/opportunities" element={<AuthGuard requiredType="district"><DistrictOpportunities /></AuthGuard>} />
                <Route path="/district/integrations" element={<AuthGuard requiredType="district"><DistrictIntegrations /></AuthGuard>} />
                <Route path="/district/settings" element={<AuthGuard requiredType="district"><DistrictSettings /></AuthGuard>} />
                <Route path="/district/languages" element={<AuthGuard requiredType="district"><DistrictLanguages /></AuthGuard>} />

                {/* ROI Intelligence Platform */}
                <Route path="/district/initiatives" element={<AuthGuard requiredType="district"><DistrictInitiatives /></AuthGuard>} />
                <Route path="/district/cascade" element={<AuthGuard requiredType="district"><DistrictCascadePortfolio /></AuthGuard>} />
                <Route path="/district/cascade/new" element={<AuthGuard requiredType="district"><NewInitiativeWizard /></AuthGuard>} />
                <Route path="/district/cascade/:id" element={<AuthGuard requiredType="district"><DistrictCascadeInitiative /></AuthGuard>} />
                <Route path="/district/registry" element={<AuthGuard requiredType="district"><EdTechRegistry /></AuthGuard>} />
                <Route path="/district/registry/compare" element={<AuthGuard requiredType="district"><RegistryCompare /></AuthGuard>} />
                <Route path="/district/tech-stack" element={<AuthGuard requiredType="district"><DistrictTechStackReport /></AuthGuard>} />
                <Route path="/district/tech-inventory" element={<AuthGuard requiredType="district"><DistrictTechStackInventory /></AuthGuard>} />
                <Route path="/district/tech-utilization" element={<AuthGuard requiredType="district"><DistrictTechStackUtilization /></AuthGuard>} />
                <Route path="/district/tech-roi" element={<AuthGuard requiredType="district"><DistrictTechStackROI /></AuthGuard>} />
                <Route path="/district/investment-matrix" element={<AuthGuard requiredType="district"><DistrictInvestmentMatrix /></AuthGuard>} />
                <Route path="/district/strategy" element={<AuthGuard requiredType="district"><DistrictStrategicPlanner /></AuthGuard>} />
                <Route path="/district/strategy/constrained" element={<AuthGuard requiredType="district"><DistrictStrategicConstrained /></AuthGuard>} />
                <Route path="/district/budget-scenarios" element={<AuthGuard requiredType="district"><DistrictBudgetScenario /></AuthGuard>} />
                <Route path="/district/endpoint-agents" element={<AuthGuard requiredType="district"><DistrictEndpointAgents /></AuthGuard>} />

                {/* Workflow Builder */}
                {/* The HITL task queue — the door workflow_human_tasks never had. */}
                <Route path="/district/tasks" element={<AuthGuard requiredType="district"><DistrictTasks /></AuthGuard>} />
                <Route path="/district/workflows" element={<AuthGuard requiredType="district"><DistrictWorkflowList /></AuthGuard>} />
                <Route path="/district/workflows/new" element={<AuthGuard requiredType="district"><DistrictWorkflowBuilder /></AuthGuard>} />
                <Route path="/district/workflows/:id" element={<AuthGuard requiredType="district"><DistrictWorkflowDetail /></AuthGuard>} />
                <Route path="/district/workflows/:id/runs/:runId" element={<AuthGuard requiredType="district"><DistrictWorkflowRunDetail /></AuthGuard>} />
                <Route path="/district/workflows/:id/edit" element={<AuthGuard requiredType="district"><DistrictWorkflowBuilder /></AuthGuard>} />
                <Route path="/district/workflow-templates" element={<AuthGuard requiredType="district"><DistrictTemplateGallery /></AuthGuard>} />
                <Route path="/district/resources" element={<AuthGuard requiredType="district"><DistrictResourceCatalog /></AuthGuard>} />
                <Route path="/district/partners" element={<AuthGuard requiredType="district"><DistrictPartnerNetwork /></AuthGuard>} />
                {/* Sprint 4 PR-4d — district↔partner contracts (read = fiscal set + counselor; write enforced server-side) */}
                <Route path="/district/contracts" element={<AuthGuard requiredType="district"><DistrictContracts /></AuthGuard>} />
                <Route path="/marketplace/admin" element={<AuthGuard requiredType="district"><MarketplaceAdmin /></AuthGuard>} />
                <Route path="/marketplace/partner-vetting" element={<AuthGuard requiredType="district"><MarketplacePartnerVetting /></AuthGuard>} />
                <Route path="/marketplace/partner-vetting/:orgId" element={<AuthGuard requiredType="district"><MarketplacePartnerVettingDetail /></AuthGuard>} />
                <Route path="/district/context" element={<AuthGuard requiredType="district"><DistrictContextConfig /></AuthGuard>} />
                <Route path="/district/context-graph-policy" element={<AuthGuard requiredType="district"><DistrictContextGraphPolicy /></AuthGuard>} />
                <Route path="/district/mtss-setup" element={<AuthGuard requiredType="district"><DistrictMtssSetup /></AuthGuard>} />
                <Route path="/district/benefit-coverage" element={<AuthGuard requiredType="district"><DistrictBenefitCoverage /></AuthGuard>} />
                <Route path="/district/benefits-review" element={<AuthGuard requiredType="district"><DistrictBenefitsReview /></AuthGuard>} />

                {/* Workspace Admin */}
                {/* Paths are relative to BrowserRouter basename="/admin" — do not re-prefix with /admin. */}
                <Route path="/workspace/roles" element={<AuthGuard requiredType="district"><RoleTemplatesPage /></AuthGuard>} />
                <Route path="/workspace/roles/:role_id" element={<AuthGuard requiredType="district"><RoleTemplateEditor /></AuthGuard>} />
                <Route path="/workspace/locks" element={<AuthGuard requiredType="district"><LockManagerPage /></AuthGuard>} />
                <Route path="/workspace/preview" element={<AuthGuard requiredType="district"><PreviewModePage /></AuthGuard>} />
                <Route path="/workspace/items" element={<AuthGuard requiredType="district"><ItemsCatalogPage /></AuthGuard>} />
                <Route path="/workspace/items/new" element={<AuthGuard requiredType="district"><WebPartBuilderPage /></AuthGuard>} />
                <Route path="/workspace/items/:itemCode/edit" element={<AuthGuard requiredType="district"><WebPartBuilderPage /></AuthGuard>} />
                <Route path="/workspace/layouts" element={<AuthGuard requiredType="district"><LayoutLibraryPage /></AuthGuard>} />
                <Route path="/workspace/vendors/:vendorCode" element={<AuthGuard requiredType="district"><VendorAppPage /></AuthGuard>} />

                {/* Redirects */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<NotFound />} />

                {/* Partner Portal — public */}
                <Route path="/partner-portal/login"          element={<PartnerPortalLogin />} />
                <Route path="/partner-portal/register"       element={<PartnerPortalRegister />} />
                <Route path="/partner-portal/setup-2fa"      element={<PartnerPortalSetup2FA />} />

                {/* Superuser entry — admin login required */}
                <Route path="/partner-portal/switch"         element={<PartnerPortalSwitch />} />

                {/* Partner Portal — authenticated */}
                <Route path="/partner-portal" element={<PartnerPortalAuthGuard><PartnerPortalDashboard /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/programs"    element={<PartnerPortalAuthGuard><PartnerPortalPrograms /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/programs/:agentId/profile" element={<PartnerPortalAuthGuard><PartnerPortalProgramProfile /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/referrals"   element={<PartnerPortalAuthGuard><PartnerPortalReferrals /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/applications" element={<PartnerPortalAuthGuard><PartnerPortalApplications /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/enrollments" element={<PartnerPortalAuthGuard><PartnerPortalRoster /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/attendance"  element={<PartnerPortalAuthGuard><PartnerPortalAttendance /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/analytics"   element={<PartnerPortalAuthGuard><PartnerPortalAnalytics /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/billing"     element={<PartnerPortalAuthGuard><PartnerPortalBilling /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/team"        element={<PartnerPortalAuthGuard><PartnerPortalTeam /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/settings"    element={<PartnerPortalAuthGuard><PartnerPortalSettings /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/onboarding"  element={<PartnerPortalAuthGuard><PartnerPortalOnboarding /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/credentials" element={<PartnerPortalAuthGuard><PartnerPortalCredentials /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/how-it-works" element={<PartnerPortalAuthGuard><PartnerPortalHowItWorks /></PartnerPortalAuthGuard>} />
                <Route path="/partner-portal/guide"       element={<PartnerPortalAuthGuard><PartnerPortalGuide /></PartnerPortalAuthGuard>} />

                {/* Legacy /cbo/* → /partner-portal/* (Sprint 2 PR-2b.2 rename) */}
                <Route path="/cbo" element={<LegacyCboRedirect />} />
                <Route path="/cbo/*" element={<LegacyCboRedirect />} />

                        </Routes>
            </Suspense>
        </BrowserRouter>
    );
}

export default App;
